/**
 * The project's task list.
 *
 * A list, not a board: the roadmap tab next door is the board, and this is where
 * you go when you want to read every task in order rather than compare columns.
 * The status stays changeable from here because that is the one edit worth doing
 * without opening the task. The row itself, the assignee column and the closed
 * eye beside a hidden title live in `ProjectTaskRow.tsx`; this file is what is
 * asked for and how the answer is arranged.
 *
 * Two ways in on purpose. The one-field quick add stays in the toolbar - a
 * dialog for typing six words is a dialog nobody opens - and next to it sits the
 * full dialog, which is the only place a task can be given a start date, an
 * estimate or a non-billable flag. Before that button existed, every task
 * created from a project was a title with a default billable flag nobody chose.
 *
 * MILESTONES ARE SHOWN BY GROUPING, NOT BY A COLUMN, and only when the grouping
 * divides something. 71 of the 112 tasks in production carry a milestone id that
 * no screen has ever drawn, so "which phase is this under" needed an answer;
 * a seventh column was the wrong place to put it. A column repeats one phase
 * name down forty consecutive rows, spends a full track doing it and pushes the
 * list past the width the card can draw without a sideways scrollbar - which is
 * the same argument the closed eye makes against a `visibleToClient` column.
 * A heading writes the name once, directly above the rows it is true of, and
 * costs no width at all.
 *
 * "Only when it divides something" is the whole rule: headings are drawn when
 * the rows on screen fall into two or more groups, and never otherwise. So a
 * project with one phase and forty tasks stays a plain list rather than becoming
 * forty rows under one heading plus an empty heading for the unfiled ones. THE
 * FAILURE MODE is the mirror of that: when every task really is under the same
 * phase, this list looks exactly like a project that has no phases at all.
 * Nothing on screen lies - no heading claims anything - but the fact goes
 * unsaid, and the only thing left saying it is the milestone filter, which is
 * drawn whenever the project has one and names them all in its menu. That is the
 * trade taken deliberately: a heading that carves nothing is read as furniture
 * within a week, and once the eye has learned to skip headings it skips the ones
 * that matter too.
 *
 * The unfiled group is a real group and is drawn LAST. The list endpoint keeps a
 * reserved filter value for it ("none") precisely because tasks under no phase
 * are the ones that go missing, so the filter offers it as a choice instead of
 * only offering the phases - a filter that cannot ask "what is unfiled" leaves
 * the operator hunting for rows that were on screen the whole time.
 *
 * Grouping needs the project's milestones, and until that request lands there
 * are none: every row falls into the unfiled bucket, that is one group, and one
 * group draws no headings. A milestone the screen cannot name never gets a
 * heading of its own - the same rule the milestones tab follows for its counts.
 *
 * There is no assignee filter here, unlike the task register. Every task in this
 * database names nobody (the import found one host user and refused to invent
 * owners), so the control would offer a single name that returns an empty list
 * on every project, and it would sit in a toolbar that already carries a form
 * and two buttons. The column answers "who", which is the question this tab was
 * missing; filtering by a person across projects is what the task register is
 * for.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ListTodo, Loader2, Plus } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  createTask, getProject, getTasks, setTaskStatus,
  type Milestone, type Project, type Task, type TaskStatus,
} from "../../../lib/projects";
import { Btn, EmptyState, MenuField, Toolbar, listCard } from "../../ui";
import { NewTaskModal } from "../tasks/NewTaskModal";
import { ProjectTaskHead, ProjectTaskRow, TASK_COLS } from "./ProjectTaskRow";

/**
 * How many rows this tab asks for. The list is not paged - a project's tasks are
 * read in one go, and a "page 2" on a tab inside a tab is a place people get
 * lost - so the footer states the ceiling instead of hiding it: a project with
 * 137 tasks says it is showing 100 of them rather than quietly ending at 100.
 */
const PAGE_SIZE = 100;

/** The filter's own value for "not filtering", which never reaches the API. */
const ALL = "all";

/**
 * The API's reserved `milestoneId` meaning "filed under no milestone".
 *
 * A value rather than an absence, because the server applies every filter behind
 * a truthiness check: an empty string there reads as "no filter" and would
 * answer the unfiled question with the whole list. Spelled out here rather than
 * imported - the client and the route are two deployables, and a constant shared
 * across that line is a constant that looks synchronised without being it.
 */
const NONE = "none";

/** The project as this tab loads it: `getProject` always includes the
 *  milestones, and the grouping below cannot express "unknown" without them. */
type LoadedProject = Project & { milestones: Milestone[] };

interface TaskGroup {
  /** Null is the unfiled bucket; the caller names it, this file only orders it. */
  milestone: Milestone | null;
  tasks: Task[];
}

/**
 * The rows in milestone order with the unfiled ones last, or null when grouping
 * would say nothing.
 *
 * Null in three cases that are really one: no milestones on the project, no
 * milestones LOADED yet, or every visible row in the same bucket. All three end
 * with a single group, and a single heading over the whole list is a label for
 * "everything", which no reader needs. The caller draws the flat list instead.
 *
 * Order comes from the project's own milestone list (the API sorts it by
 * `sortOrder` then due date), not from the order the tasks happen to arrive in,
 * so the phases read top to bottom the same way they do on the milestones tab.
 * A milestone with no tasks on this page gets no heading: an empty group is a
 * statement about the milestone, and the milestones tab is where that is made.
 *
 * A task pointing at a milestone the project does not list falls into the
 * unfiled bucket. Since R3 the column is a real foreign key and the route
 * refuses to file a task under another project's milestone, so in practice this
 * only happens in the frame before the project request lands - and in that frame
 * every row lands there together, which is the one-group case above.
 */
function groupByMilestone(tasks: Task[], milestones: Milestone[]): TaskGroup[] | null {
  const known = new Set(milestones.map((m) => m.id));
  const buckets = new Map<string, Task[]>();
  for (const task of tasks) {
    const key = task.milestoneId && known.has(task.milestoneId) ? task.milestoneId : "";
    const bucket = buckets.get(key);
    if (bucket) bucket.push(task);
    else buckets.set(key, [task]);
  }

  const groups: TaskGroup[] = [];
  for (const milestone of milestones) {
    const rows = buckets.get(milestone.id);
    if (rows) groups.push({ milestone, tasks: rows });
  }
  const unfiled = buckets.get("");
  if (unfiled) groups.push({ milestone: null, tasks: unfiled });

  return groups.length > 1 ? groups : null;
}

/**
 * The band over one group's rows.
 *
 * The count is drawn only when the whole set is on screen. The list stops at
 * `PAGE_SIZE`, and a heading that says "2 / 5 done" over a group whose other
 * thirty rows were left behind by the cap is a number with nobody behind it -
 * the footer's "showing 100 of 137" is far away at the bottom by then. The name
 * alone stays true whatever the cap did.
 */
function GroupHead({ name, done, total, counted, first }: {
  name: string;
  done: number;
  total: number;
  counted: boolean;
  /** The air over a heading is the gap between two groups, and there is no
   *  group above the first one. Passed rather than written as `first:pt-0`,
   *  because each group is a fragment: every heading is the first child of
   *  something, so the CSS pseudo-class would clear the padding on all of them. */
  first: boolean;
}) {
  const t = useT();
  return (
    <h3 className={cn("flex items-baseline gap-2.5 px-3", !first && "pt-3")}>
      <span className="crm-prow-n">{name}</span>
      {counted ? (
        <span className="crm-mut crm-tnum text-[11.5px]">
          {t("projects.milestoneTasks", { done, total })}
        </span>
      ) : null}
    </h3>
  );
}

export function ProjectTasks({ projectId, onChange }: { projectId: string; onChange: () => void }) {
  const t = useT();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [project, setProject] = useState<LoadedProject | null>(null);
  const [milestoneId, setMilestoneId] = useState(ALL);
  const [showNew, setShowNew] = useState(false);

  // A milestone id belongs to exactly one project, so a filter carried across a
  // navigation would ask for rows that cannot exist and answer with an empty
  // list that reads as a project with no tasks.
  useEffect(() => {
    setMilestoneId(ALL);
  }, [projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Filtered by the endpoint rather than by the loaded array: the array is
      // one capped page, so filtering it here would quietly mean "of the first
      // hundred" while the footer below described a different set.
      const r = await getTasks({
        projectId,
        pageSize: PAGE_SIZE,
        milestoneId: milestoneId === ALL ? undefined : milestoneId,
      });
      setTasks(r.rows);
      setTotal(r.total);
    } finally {
      setLoading(false);
    }
  }, [projectId, milestoneId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The dialog's project select needs a name, the filter and the grouping need
  // the milestones, and this tab is handed an id. Its own request rather than
  // part of `load`, so changing a task's status does not fetch the project
  // again. Until it lands the dialog stays shut: opening it with an empty
  // project list would offer "no project" as the only choice and file the task
  // away from the project it was created on.
  useEffect(() => {
    let alive = true;
    getProject(projectId)
      .then((p) => {
        if (alive) setProject(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  const groups = useMemo(
    () => groupByMilestone(tasks, project?.milestones ?? []),
    [tasks, project],
  );

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      // The filter is the destination as well as the view. A task typed while
      // one phase is on screen belongs to that phase - and the alternative is
      // worse than a surprise: created unfiled, it would fall straight out of
      // the filtered list and read as an add that did nothing. "All" and
      // "unfiled" both mean no milestone, which is what this field has always
      // created. Choosing a DIFFERENT phase is the dialog's job, not this
      // field's; the toolbar gets no picker of its own.
      const filed = milestoneId !== ALL && milestoneId !== NONE ? { milestoneId } : {};
      await createTask({ projectId, title: title.trim(), ...filed });
      setTitle("");
      await load();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function move(id: string, s: TaskStatus) {
    setTasks((prev) => prev.map((x) => (x.id === id ? { ...x, status: s } : x)));
    try {
      await setTaskStatus(id, s);
      onChange();
    } catch {
      void load();
    }
  }

  const milestones = project?.milestones ?? [];
  // Nothing is hidden by the cap, so a count of what is on screen is a count of
  // what there is. See `GroupHead`.
  const counted = tasks.length === total;

  return (
    <>
      {/* One field and a button, so it stays in the toolbar rather than becoming
          a modal: a dialog for typing six words is a dialog nobody opens. */}
      <Toolbar>
        {/* Drawn only where there is something to filter by. On a project with
            no phases the menu would offer "all" and "unfiled", which are the
            same list twice. It sits ahead of the quick add because it is the
            context that field writes into. */}
        {milestones.length ? (
          <MenuField
            value={milestoneId}
            onChange={setMilestoneId}
            label={t("projects.milestone")}
            options={[
              { value: ALL, label: t("common.all") },
              ...milestones.map((m) => ({ value: m.id, label: m.name })),
              { value: NONE, label: t("projects.noMilestone") },
            ]}
          />
        ) : null}

        <form onSubmit={add} className="flex flex-1 items-center gap-2.5">
          <label className="crm-fld flex-1">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("tasks.addPlaceholder")}
              autoComplete="off"
            />
          </label>
          <Btn type="submit" tone="dark" disabled={busy || !title.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("common.add")}
          </Btn>
        </form>
        <Btn tone="brand" onClick={() => setShowNew(true)} disabled={!project}>
          <Plus className="size-4" />
          {t("tasks.add")}
        </Btn>
      </Toolbar>

      <div className="crm-card" style={listCard(TASK_COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          // No separate "nothing matches the filter" wording: the filter pill is
          // on screen right above this card with the phase's name on it, which
          // says why the card is empty better than a second sentence would.
          <EmptyState icon={ListTodo} title={t("tasks.empty")} />
        ) : (
          <>
            <ProjectTaskHead />

            <div className="crm-rows pt-0">
              {groups
                ? groups.map((g, i) => (
                    // A fragment rather than a wrapper: `crm-rows` is the flex
                    // column that spaces these, and a div around each group
                    // would collect the rows into one item and lose the gap
                    // between them.
                    <Fragment key={g.milestone?.id ?? ""}>
                      <GroupHead
                        name={g.milestone ? g.milestone.name : t("projects.noMilestone")}
                        done={g.tasks.filter((x) => x.status === "done").length}
                        total={g.tasks.length}
                        counted={counted}
                        first={i === 0}
                      />
                      {g.tasks.map((tk) => (
                        <ProjectTaskRow key={tk.id} task={tk} onStatus={(s) => void move(tk.id, s)} />
                      ))}
                    </Fragment>
                  ))
                : tasks.map((tk) => (
                    <ProjectTaskRow key={tk.id} task={tk} onStatus={(s) => void move(tk.id, s)} />
                  ))}
            </div>

            <div className="crm-tfoot">{t("projects.showingTasks", { shown: tasks.length, n: total })}</div>
          </>
        )}
      </div>

      {showNew && project ? (
        <NewTaskModal
          projects={[project]}
          defaultProjectId={project.id}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            void load();
            onChange();
          }}
        />
      ) : null}
    </>
  );
}
